import React, { useEffect, useState } from "react"
import { useTranslation } from 'react-i18next'
import { Dropdown } from 'react-bootstrap'
import Select from 'react-select'

import FilterSearch from 'pages/parts/FilterSearch'
import FilterPagination from 'pages/parts/FilterPagination'
import useFilterSearchPagination from 'pages/parts/useFilterSearchPagination'
import PeriodPicker from 'pages/parts/PeriodPicker'

import { formatMoney, formatDate, returnSign } from "helpers/helpers"
import { httpOk, GET } from 'helpers/api'

function Index() {
	const { t } = useTranslation()
	const date = new Date()

	const [data, setData] = useState({})
	const [partners, setPartners] = useState([])
	const [dropdownIsOpen, setDropdownIsOpen] = useState(false)
	const {
		filterData,
		setFilterData,
		filterDataExist,
		setFilterDataExist,
		pagination,
		setPagination,
		applySearch,
		applyFilters,
		paginate,
	} = useFilterSearchPagination({
		initialFilterData: {
			start_date: formatDate(new Date(date.getFullYear(), date.getMonth(), 1), 'yyyy-MM-dd'),
			end_date: formatDate(new Date(), 'yyyy-MM-dd'),
			search: '',
			partner_login: null,
			page: null,
		},
		initialPagination: {
			url: '/admin/partner-bonus-histories',
			response: null
		},
	})

	async function searchWithFilter(nextFilterData = filterData) {
		var filterDataCopy = { ...nextFilterData }
		var filterUrl = "/admin/partner-bonus-histories"
		var urlParams = ""
		checkFilter(filterDataCopy)

		if (filterDataCopy.search)
			urlParams += returnSign(urlParams) + 'search=' + filterDataCopy.search
		if (filterDataCopy.partner_login)
			urlParams += returnSign(urlParams) + 'partner_login=' + encodeURIComponent(filterDataCopy.partner_login)
		if (filterDataCopy.start_date)
			urlParams += returnSign(urlParams) + 'start_date=' + filterDataCopy.start_date
		if (filterDataCopy.end_date)
			urlParams += returnSign(urlParams) + 'end_date=' + filterDataCopy.end_date
		if (filterDataCopy.page)
			urlParams += returnSign(urlParams) + 'page=' + filterDataCopy.page

		if (`/admin/partner-bonus-histories${urlParams}` !== filterUrl)
			setFilterDataExist(true)

		filterUrl += urlParams

		const response = await GET(filterUrl, {}, { loader: true })
		if (httpOk(response)) {
			setData(response.data)
			setPagination({ ...pagination, 'url': filterUrl, 'response': response.data })
		}

		setDropdownIsOpen(false)
	}

	function checkFilter(currentFilterData) {
		if (
			!currentFilterData.search
			&& !currentFilterData.page
			&& !currentFilterData.partner_login
		) {
			setFilterDataExist(false)
		}
	}

	function toggleDropdown() {
		setDropdownIsOpen(!dropdownIsOpen)
	}

	function handlePaginate(page) {
		paginate(page)
		searchWithFilter({ ...filterData, page })
	}

	async function getPartners() {
		const response = await GET('/admin/helper/partners')
		if (httpOk(response)) setPartners(response.data || [])
	}

	useEffect(() => {
		getPartners()
		searchWithFilter()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	const selectedPartner = partners.find((option) => option.login === filterData.partner_login)

	return (
		<>
			<div className="page-header d-flex justify-content-between">
				<div className="vertical-center">
					<h4>{t('admin_partner_bonus_histories')} [{data.total}]</h4>
				</div>
			</div>

			<div className="card">
				<div className="card-body">
					<div className="d-flex justify-content-between mb-2">
						<div className="d-flex flex-wrap gap-2">
							<div>
								<FilterSearch
									filterData={filterData}
									onSearchChange={(search) => applySearch(search, (nextFilterData) => {
										searchWithFilter(nextFilterData)
									})}>
								</FilterSearch>
							</div>
							<div>
								<Dropdown onToggle={toggleDropdown} show={dropdownIsOpen} className="h-fit-content">
									<Dropdown.Toggle variant="outline-primary" className="btn-wide h-100">
										{t('filter')}
									</Dropdown.Toggle>

									<Dropdown.Menu className="dropdown-filter">
										<div className="row">
											<div className="form-group col-md-6">
												<label>{t('partners')}</label>
												<Select
													isClearable
													options={partners}
													value={selectedPartner || null}
													onChange={(option) => setFilterData({
														...filterData,
														'partner_login': option ? option.login : null,
													})}
													placeholder=""
													noOptionsMessage={() => t('list_empty')}
													getOptionLabel={(option) => option.name
														? `${option.name} (${option.login})`
														: option.login}
													getOptionValue={(option) => option.login}
												/>
											</div>
										</div>
										<div className="row">
											<div className="form-group col-md-12">
												<label>{t('period')}</label>
												<PeriodPicker
													startDate={filterData.start_date}
													endDate={filterData.end_date}
													onChange={(period) => setFilterData({ ...filterData, ...period })}
													withTime={false}
													minDate={new Date(new Date().getFullYear() - 1, 0, 1)}
													maxDate={new Date(new Date().getFullYear(), 11, 31)}
												/>
											</div>
										</div>
										<div className="row mt-3">
											<div className="col-md-12">
												<div className="d-flex justify-content-end gap-2">
													<button className="btn btn-secondary"
														onClick={() => setDropdownIsOpen(false)}>
														{t('cancel')}
													</button>
													<button className="btn btn-primary"
														onClick={() => applyFilters((nextFilterData) => {
															searchWithFilter(nextFilterData)
														})}>
														{t('apply')}
													</button>
												</div>
											</div>
										</div>
									</Dropdown.Menu>
								</Dropdown>
							</div>
						</div>
						<div className="vertical-center">
							<h5 className="d-flex justify-content-between gap-3">
								<b>{t('amount')}:</b>{' '}
								<span className={Number(data.total_amount) < 0 ? 'text-danger' : ''}>
									{formatMoney(data.total_amount)}
								</span>
							</h5>
						</div>
					</div>

					{filterDataExist &&
						<div className="filter-block">
							<div className="filter">
								{(filterData.start_date || filterData.end_date) &&
									<span className="me-2">
										<b>{t('period')}: </b>
										{filterData.start_date &&
											<span>{formatDate(filterData.start_date)}</span>
										}
										{(filterData.start_date && filterData.end_date) &&
											<b> - </b>
										}
										{filterData.end_date &&
											<span>{formatDate(filterData.end_date)}</span>
										}
									</span>
								}
								{selectedPartner &&
									<span className="me-2">
										<b>{t('partners')}: </b>
										<span>{selectedPartner.name || selectedPartner.login}</span>
									</span>
								}
								{filterData.search &&
									<span className="me-2">
										<b>{t('search')}: </b>
										<span>{filterData.search}</span>
									</span>
								}
							</div>
						</div>
					}

					<div className="table-responsive">
						<table className="table table-bordered mb-0">
							<thead>
								<tr>
									<th style={{ 'minWidth': '33px' }} className="text-center">#</th>
									<th style={{ 'width': '18%' }}>{t('partners')}</th>
									<th style={{ 'width': '12%' }}>{t('login')}</th>
									<th style={{ 'width': '18%' }}>{t('pos')}</th>
									<th style={{ 'width': '18%' }}>{t('note')}</th>
									<th style={{ 'width': '12%' }} className="text-center">{t('date')}</th>
									<th style={{ 'width': '8%' }} className="text-end">%</th>
									<th style={{ 'width': '12%' }} className="text-end">{t('amount')}</th>
								</tr>
							</thead>
							<tbody>
								{data?.data?.map((item, index) => (
									<tr key={item.id || index}>
										<td className="text-center">{item.row_number}</td>
										<td className="td-ellipsis" title={item.partner_name}>{item.partner_name}</td>
										<td className="td-ellipsis">{item.login}</td>
										<td className="td-ellipsis" title={item.pos_name}>{item.pos_name}</td>
										<td className="td-ellipsis" title={item.note}>{item.note}</td>
										<td className="text-center">{formatDate(item.created_at, 'dd.MM.yy HH:mm')}</td>
										<td className="text-end">{formatMoney(item.partner_percentage)}</td>
										<td className={`text-end ${Number(item.display_amount ?? item.partner_amount) < 0 ? 'text-danger' : ''}`}>
											{formatMoney(item.display_amount ?? item.partner_amount)}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>

					{pagination.response &&
						<FilterPagination
							pagination={pagination}
							paginate={handlePaginate}>
						</FilterPagination>
					}
				</div>
			</div>
		</>
	)
}

export default Index
